import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, LogOut, FileText, Scissors, Plane, Image as ImageIcon, Stamp, FileUser } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import ToolSidebar from '../components/ToolSidebar';
import '../styles/TourismSoftwarePage.css';

const tools = [
  {
    title: 'PDF Editor',
    titleKey: 'pdfEditorTool',
    desc: 'Edit, redact and clean PDF and image documents. Remove or replace phone numbers automatically.',
    descKey: 'pdfEditorDesc',
    icon: FileText,
    link: '/client/pdfeditor',
    color: '#3B82F6',
  },
  {
    title: 'Background Remover',
    titleKey: 'bgRemoverTool',
    desc: 'Remove image backgrounds instantly with AI. Choose custom colors and download in PNG or JPG.',
    descKey: 'bgRemoverDesc',
    icon: Scissors,
    link: '/bgremover',
    color: '#8B5CF6',
  },
  {
    title: 'Dummy Flight Ticket',
    titleKey: 'dummyFlightTool',
    desc: 'Generate realistic sample flight tickets for visa applications. Any route worldwide.',
    descKey: 'dummyFlightDesc',
    icon: Plane,
    link: '/keyflight',
    color: '#E11D48',
  },
  {
    title: 'Image Resizer',
    titleKey: 'imageResizerTool',
    desc: 'Resize, compress and convert images. Reduce file size, change dimensions and format instantly.',
    descKey: 'imageResizerDesc',
    icon: ImageIcon,
    link: '/imageresize',
    color: '#10B981',
  },
  {
    title: 'Stamp Maker Tool',
    titleKey: 'stampMakerTool',
    desc: 'Design and create custom digital rubber stamps for your business documents and official papers.',
    descKey: 'stampMakerDesc',
    icon: Stamp,
    link: '/stampmaker',
    color: '#D97706',
  },
  {
    title: 'Resume Builder',
    titleKey: 'resumeBuilderTool',
    desc: 'Build a professional resume or CV online for free. Choose modern templates and download as PDF.',
    descKey: 'resumeBuilderDesc',
    icon: FileUser,
    link: '/resume',
    color: '#6366F1',
  },
];

const TourismSoftwarePage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const seo = useSEO('tourismsoftware', {
    title: 'Free Online Tools for Travelers | Background Remover, Image Resizer, Flight Ticket — Registan Tourism',
    description: 'Use free online travel tools by Registan Tourism: AI background remover, image resizer, dummy flight ticket generator, PDF editor, and visa checker.',
    keywords: 'free travel tools online, passport photo background remover, image resizer for visa, dummy flight ticket generator'
  });
  const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;

  return (
    <div className="ts-page" data-testid="tourism-software-page">
      <Helmet>
        <title>Free Online Tools for Travel, Visa & Business — Tourism Software | Registan Tourism</title>
        <meta name="description" content="Free online tools: PDF editor, background remover, resume builder, stamp maker, image resizer and dummy flight tickets. No signup required. Built for travelers, visa applicants and businesses." />
        <meta name="keywords" content="tourism software, free online tools, PDF editor, background remover, resume builder, stamp maker, image resizer, dummy flight ticket, visa tools, UAE business tools" />
        <link rel="canonical" href="https://registantours.com/tourismsoftware" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Free Online Tools for Travel, Visa & Business — Tourism Software | Registan Tourism" />
        <meta property="og:description" content="Free online tools: PDF editor, background remover, resume builder, stamp maker, image resizer and dummy flight tickets. No signup required." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/tourismsoftware" />
        <meta property="og:site_name" content="Registan Tourism" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Header */}
      <header className="dark-header" data-testid="ts-header">
        <div className="header-left">
          <div className="header-logo">
            <Link to="/"><img src={currentLogo} alt="Registan" className="logo-img" /></Link>
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/tourismsoftware" className="dark-nav-link active">{t('tourismSoftware') || 'Tourism Software'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>

        {isAuthenticated ? (
          <div className="header-user-menu">
            <NotificationBell userEmail={user?.email} />
            <button className="btn-user-profile" onClick={() => window.location.href = '/dashboard'}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => window.location.href = '/login'}>
              {t('login')}
            </button>
            <button className="btn-primary btn-signup" onClick={() => window.location.href = '/register'}>
              {t('signUp')}
            </button>
          </div>
        )}

        <button
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(true)}
          data-testid="ts-mobile-menu-toggle"
        >
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/tourismsoftware" />

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="ts-main tool-page-content">
        <div className="ts-hero">
          <h1 className="ts-title" data-testid="ts-title">{t('tourismSoftwareTitle') || 'Free Online Tools for Travel, Visa & Business — Tourism Software by Registan'}</h1>
          <h2 className="ts-subtitle" data-testid="ts-subtitle">{t('tourismSoftwareSubtitle') || 'PDF Editor, Background Remover, Resume Builder, Stamp Maker & More — All Free, No Signup Required'}</h2>
          <p className="ts-description" data-testid="ts-description">
            Registan Tourism offers a powerful suite of free online tools designed for travelers, visa applicants,
            freelancers, and businesses worldwide. Edit and redact PDF documents, remove phone numbers and
            sensitive data from files, create professional resumes, design custom rubber stamps for official
            papers, resize and compress images, and generate dummy flight tickets for visa applications.
            Every tool is completely free with no registration required — simply upload your file, make changes,
            and download instantly. Built with security in mind, all uploads are processed and deleted automatically.
            Whether you are starting a business in Dubai, applying for a UAE visa, or managing documents
            for your company, our tools save you time and money.
          </p>
        </div>

        <div className="ts-grid" data-testid="ts-tools-grid">
          {tools.map((tool) => (
            <Link to={tool.link} key={tool.title} className="ts-card" data-testid={`ts-tool-${tool.title.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="ts-card-icon" style={{ background: `${tool.color}18`, color: tool.color }}>
                <tool.icon size={28} strokeWidth={1.8} />
              </div>
              <h2 className="ts-card-title">{t(tool.titleKey) || tool.title}</h2>
              <p className="ts-card-desc">{t(tool.descKey) || tool.desc}</p>
              <span className="ts-card-link" style={{ color: tool.color }}>{t('openTool') || 'Open Tool'} →</span>
            </Link>
          ))}
        </div>

      </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default TourismSoftwarePage;
